import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Plus, X, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type LeadNotificationFilter = 'all' | 'agent_only' | 'loan_officer_only';

interface NotificationRecipient {
  email: string;
  filter: LeadNotificationFilter;
}

interface LeadNotificationSettingsProps {
  siteId: string;
}

// Helper to parse notification emails from DB
function parseNotificationEmails(data: unknown): NotificationRecipient[] {
  if (!data || !Array.isArray(data)) return [];
  return data.map((item: unknown) => {
    if (typeof item === 'string') {
      return { email: item, filter: 'all' as LeadNotificationFilter };
    }
    if (typeof item === 'object' && item !== null && 'email' in item) {
      const obj = item as { email: string; filter?: string };
      return {
        email: obj.email,
        filter: (obj.filter as LeadNotificationFilter) || 'all',
      };
    }
    return null;
  }).filter((r): r is NotificationRecipient => r !== null);
}

const filterLabels: Record<LeadNotificationFilter, string> = {
  all: 'All',
  agent_only: 'Agent',
  loan_officer_only: 'Loan Officer',
};

export function LeadNotificationSettings({ siteId }: LeadNotificationSettingsProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newFilter, setNewFilter] = useState<LeadNotificationFilter>('all');

  const canSave = !enabled || recipients.length > 0;

  useEffect(() => {
    if (siteId) {
      fetchSettings();
    }
  }, [siteId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('lead_notification_enabled, lead_notification_emails')
        .eq('id', siteId)
        .single();

      if (error) throw error;

      setEnabled(data?.lead_notification_enabled ?? false);
      setRecipients(parseNotificationEmails(data?.lead_notification_emails));
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!canSave) {
      toast({
        title: 'Add a recipient email',
        description: 'Click the + button to add at least one notification recipient before saving.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('sites')
        .update({
          lead_notification_enabled: enabled,
          lead_notification_emails: recipients,
        } as any)
        .eq('id', siteId);

      if (error) throw error;

      toast({
        title: 'Settings saved',
        description: 'Lead notification settings have been updated.',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    if (recipients.some(r => r.email === trimmed)) {
      toast({
        title: 'Duplicate email',
        description: 'This email is already in the list.',
        variant: 'destructive',
      });
      return;
    }

    setRecipients([...recipients, { email: trimmed, filter: newFilter }]);
    setNewEmail('');
    setNewFilter('all');
  };

  const removeRecipient = (emailToRemove: string) => {
    setRecipients(recipients.filter(r => r.email !== emailToRemove));
  };

  const updateRecipientFilter = (email: string, filter: LeadNotificationFilter) => {
    setRecipients(recipients.map(r => 
      r.email === email ? { ...r, filter } : r
    ));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Lead Email Notifications
        </CardTitle>
        <CardDescription>
          Send email notifications when leads are captured on this site
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="lead-notifications" className="flex flex-col gap-1">
            <span>Enable Lead Notifications</span>
            <span className="font-normal text-sm text-muted-foreground">
              Receive an email when someone submits a form
            </span>
          </Label>
          <Switch
            id="lead-notifications"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="space-y-4 pt-2 border-t">
            <div className="space-y-3">
              <Label>Notification Recipients</Label>
              <p className="text-xs text-muted-foreground">
                Assign each recipient to receive All leads, Agent leads only, or Loan Officer leads only.
              </p>
              
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  className="flex-1"
                />
                <Select value={newFilter} onValueChange={(v: LeadNotificationFilter) => setNewFilter(v)}>
                  <SelectTrigger className="w-[130px] bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background border shadow-lg z-50">
                    <SelectItem value="all">All Leads</SelectItem>
                    <SelectItem value="agent_only">Agent Only</SelectItem>
                    <SelectItem value="loan_officer_only">Loan Officer</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={addRecipient}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {recipients.length > 0 ? (
                <div className="space-y-2">
                  {recipients.map((recipient) => (
                    <div key={recipient.email} className="flex items-center gap-2 bg-muted/50 p-2 rounded-md">
                      <span className="flex-1 text-sm truncate">{recipient.email}</span>
                      <Select 
                        value={recipient.filter} 
                        onValueChange={(v: LeadNotificationFilter) => updateRecipientFilter(recipient.email, v)}
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background border shadow-lg z-50">
                          <SelectItem value="all">All Leads</SelectItem>
                          <SelectItem value="agent_only">Agent Only</SelectItem>
                          <SelectItem value="loan_officer_only">Loan Officer</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        onClick={() => removeRecipient(recipient.email)}
                        className="ml-1 hover:bg-muted rounded p-0.5"
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No recipients added. Add at least one email address.
                </p>
              )}
            </div>
          </div>
        )}

        <Button onClick={saveSettings} disabled={saving || !canSave} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Notification Settings
        </Button>
      </CardContent>
    </Card>
  );
}
